/* Hallmark · component: auth-primitives · genre: modern-minimal · theme: project system
 * (Outfit display · Geist body · indigo accent — preserved from src/index.css)
 * states: focus ring + transitions shared by every auth control
 * pre-emit critique: P5 H4 E5 S4 R5 V4
 */

/**
 * Shared building blocks for pdf-api's auth screens.
 *
 * Five screens (login, register, forgot-password, verify-email, consent) now
 * live in this app. Copying the same heading block and the same alert into each
 * one is exactly the templating the audit flagged in the onboarding trio, so the
 * pieces they share live here once — the screens stay siblings by construction
 * rather than by discipline.
 *
 * What is deliberately NOT here: anything screen-specific. These are primitives,
 * not a page template; each screen still composes its own rhythm.
 *
 * FIELD_RING and CONTROL_TRANSITION have outgrown the auth folder — the
 * settings pages consume them too, so every focus ring and control transition
 * in the app stays one definition. If a third area joins, move these two to a
 * neutral home (src/lib/) rather than importing "auth" UI ever wider.
 */

import { cn } from '@xhubio-saas/core-frontend/ui-components'
import { AlertCircle, CheckCircle, Info } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Focus ring for every interactive control on the auth screens: 2px at 2px
 * offset, applied instantly. Transitions elsewhere name their properties so the
 * ring is never among them — a ring that fades in leaves keyboard users without
 * an indicator for the length of the fade.
 */
export const FIELD_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

/** Colour/transform shifts only, on the project's motion tokens. */
export const CONTROL_TRANSITION =
  'transition-colors duration-[var(--dur-micro)] ease-[var(--ease-out)]'

/**
 * The screen's opening: title + one line of orientation. Left-aligned and roman
 * with the landing DESIGN.md's tight display tracking — the kernel's centred
 * 4xl title is the "centred everything" tell these screens are moving away from.
 */
export function AuthHeading({ title, description }: { title: string; description?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <h1 className="font-heading text-3xl tracking-[-0.04em]">{title}</h1>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  )
}

/**
 * The banners, one per tone. `role` differs on purpose: an error interrupts
 * (`alert`); a confirmation or a plain notice does not (`status`).
 *
 * `info` is the neutral notice — nothing failed, nothing succeeded (e.g. "the
 * session has ended", Plan CORE 09-26/01 S2b). It uses the app's accent
 * (`primary`), since the token system has no separate info colour.
 */
const ALERT_TONES = {
  error: {
    role: 'alert',
    Icon: AlertCircle,
    box: 'border-destructive/30 bg-destructive/10',
    icon: 'text-destructive',
  },
  success: {
    role: 'status',
    Icon: CheckCircle,
    box: 'border-success/30 bg-success/10',
    icon: 'text-success',
  },
  info: {
    role: 'status',
    Icon: Info,
    box: 'border-primary/30 bg-primary/10',
    icon: 'text-primary',
  },
} as const

export function AuthAlert({
  tone,
  children,
}: {
  tone: keyof typeof ALERT_TONES
  children: ReactNode
}) {
  const { role, Icon, box, icon } = ALERT_TONES[tone]
  return (
    <div
      role={role}
      className={cn('flex items-start gap-2 rounded-md border px-3 py-2 text-sm', box)}
    >
      <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', icon)} />
      <span className="text-foreground">{children}</span>
    </div>
  )
}

/** A quiet text link — the "back to login" / "forgot password" register. */
export function AuthTextLink({ className, ...props }: React.ComponentProps<'a'>) {
  return (
    <a
      className={cn(
        'rounded-sm text-sm text-muted-foreground underline-offset-4',
        'hover:text-foreground hover:underline',
        CONTROL_TRANSITION,
        FIELD_RING,
        className
      )}
      {...props}
    />
  )
}
